import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppThunk } from '../store'
import {
  getWorkingTime,
  getUserData,
  getLineChart,
  getWorkDetail,
  getReportExists,
  getTimeLine,
} from '../../apis/teamReport'

import {
  getReportTeam,
} from '../../apis/team'

import { setErrors } from '../common/errorsSlice'
import {
  TeamCondition,
  DailySearchCondition,
  Team,
  TeamReportWorkingTime,
  TeamReportLineChart,
  TeamReportWorkDetail,
  TeamReportTimeLine,
} from '../../types/team'

import {
  UserData,
  DataExists,
} from '../../types/report'

interface ReportState {
  evaluationExists: boolean
  userProcessExists: boolean
  team: Team
  workingTime: TeamReportWorkingTime
  userData: UserData[]
  lineChartData: TeamReportLineChart
  workDetail: TeamReportWorkDetail
  timeLine: TeamReportTimeLine[]
  isTeamLoading: boolean
  isWorkingTimeLoading: boolean
  isUserDataLoading: boolean
  isLineChartDataLoading: boolean
  isWorkDetailLoading: boolean
  isTimeLineLoading: boolean
  isDisApprove: boolean | undefined
  isDataExistsLoading: boolean,
}

const initialState: Partial<ReportState> = {
  evaluationExists: undefined,
  userProcessExists: undefined,
  isTeamLoading: true,
  isWorkingTimeLoading: true,
  isUserDataLoading: true,
  isLineChartDataLoading: true,
  isWorkDetailLoading: true,
  isTimeLineLoading: true,
  isDisApprove: undefined,
  isDataExistsLoading: true,
}

const teamReportSlice = createSlice({
  name: 'teamReport',
  initialState,
  reducers: {
    requestTeamStart: (state) => {
      state.isTeamLoading = true
      return state
    },
    requestWorkingTimeStart: (state) => {
      state.isWorkingTimeLoading = true
      return state
    },
    requestUserDataStart: (state) => {
      state.isUserDataLoading = true
      return state
    },
    requestLineChartDataStart: (state) => {
      state.isLineChartDataLoading = true
      return state
    },
    requestWorkDetailStart: (state) => {
      state.isWorkDetailLoading = true
      return state
    },
    requestTimeLineStart: (state) => {
      state.isTimeLineLoading = true
      return state
    },
    requestDataExistsStart: (state) => {
      state.isDataExistsLoading = true
      return state
    },
    buildTeamReportDataExists(state, action: PayloadAction<DataExists>) {
      state.isDataExistsLoading = false
      state.evaluationExists = action.payload.evaluation_exists
      state.userProcessExists = action.payload.user_process_exists
      return state
    },
    buildTeamSuccess(state, action: PayloadAction<Team>) {
      state.isTeamLoading = false
      state.team = action.payload
      return state
    },
    buildTeamReportWorkingTimeSuccess: (state, action: PayloadAction<TeamReportWorkingTime>) => {
      state.isWorkingTimeLoading = false
      state.workingTime = action.payload
      return state
    },
    buildTeamReportUserDataSuccess: (state, action: PayloadAction<UserData[]>) => {
      state.isUserDataLoading = false
      state.userData = action.payload
      return state
    },
    buildTeamReportLineChartDataSuccess: (state, action: PayloadAction<TeamReportLineChart>) => {
      state.isLineChartDataLoading = false
      state.lineChartData = action.payload
      return state
    },
    buildTeamReportWorkDetailSuccess: (state, action: PayloadAction<TeamReportWorkDetail>) => {
      state.isWorkDetailLoading = false
      state.workDetail = action.payload
      return state
    },
    buildTeamReportTimeLineSuccess: (state, action: PayloadAction<TeamReportTimeLine[]>) => {
      state.isTimeLineLoading = false
      state.timeLine = action.payload
      return state
    },
    setTeamReportApproval: (state) => {
      state.isDataExistsLoading = false
      state.isDisApprove = false
      return state
    },
    setTeamReportDisApproval: (state) => {
      state.isTeamLoading = false
      state.isWorkingTimeLoading = false
      state.isUserDataLoading = false
      state.isLineChartDataLoading = false
      state.isWorkDetailLoading = false
      state.isTimeLineLoading = false
      state.evaluationExists = false
      state.userProcessExists = false
      state.isDisApprove = true
      return state
    },
    requestFailure: (state) => state,
    resetState: (state) => {
      // eslint-disable-next-line no-param-reassign
      state = initialState
      return state
    },
  },
})

export const {
  requestTeamStart,
  requestWorkingTimeStart,
  requestUserDataStart,
  requestLineChartDataStart,
  requestWorkDetailStart,
  requestTimeLineStart,
  requestDataExistsStart,
  buildTeamSuccess,
  buildTeamReportWorkingTimeSuccess,
  buildTeamReportUserDataSuccess,
  buildTeamReportLineChartDataSuccess,
  buildTeamReportWorkDetailSuccess,
  buildTeamReportTimeLineSuccess,
  buildTeamReportDataExists,
  requestFailure,
  setTeamReportApproval,
  setTeamReportDisApproval,
  resetState,
} = teamReportSlice.actions

export const dailyTeamReportReducer = teamReportSlice.reducer

export const fetchReportWorkingTime = (
  param: DailySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestWorkingTimeStart())
  getWorkingTime(param)
    .then((result: TeamReportWorkingTime) => {
      dispatch(buildTeamReportWorkingTimeSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchReportUserData = (
  param: DailySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestUserDataStart())
  getUserData(param)
    .then((result: UserData[]) => {
      dispatch(buildTeamReportUserDataSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchReportLineChart = (
  param: DailySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestLineChartDataStart())
  getLineChart(param)
    .then((result: TeamReportLineChart) => {
      dispatch(buildTeamReportLineChartDataSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchReportWorkDetail = (
  param: DailySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestWorkingTimeStart())
  getWorkDetail(param)
    .then((result: TeamReportWorkDetail) => {
      dispatch(buildTeamReportWorkDetailSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchReportTimeLine = (
  param: DailySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestTimeLineStart())
  getTimeLine(param)
    .then((result: TeamReportTimeLine[]) => {
      dispatch(buildTeamReportTimeLineSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchReportTeam = (
  param: TeamCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestTeamStart())
  getReportTeam(param)
    .then((result: Team) => {
      dispatch(buildTeamSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchTeamReportExists = (
  param: DailySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestDataExistsStart())
  getReportExists(param)
    .then((result: DataExists) => {
      dispatch(setTeamReportApproval())
      dispatch(buildTeamReportDataExists(result))
    })
    .catch((error) => {
      switch (error.status) {
        case 403:
          dispatch(setTeamReportDisApproval())
          break
        case 409:
          dispatch(setTeamReportDisApproval())
          break
        default:
          dispatch(setErrors(error))
          break
      }
    })
}
